import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Paper,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { 
  Email,
  Send,
  CheckCircle,
  School,
  VideoLibrary,
  Description,
  AutoAwesome,
  Star,
  Psychology,
  EmojiEvents,
  Group
} from '@mui/icons-material';
import { Helmet } from 'react-helmet';
// Components
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';

// Smooth animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
`;

// Main container with Resources page styling
const MainContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#fafbfc',
  minHeight: '100vh',
  position: 'relative',
  overflow: 'hidden',
  background: `
    linear-gradient(135deg, 
      rgba(42, 146, 53, 0.02) 0%, 
      rgba(76, 175, 80, 0.01) 25%,
      rgba(42, 146, 53, 0.03) 50%,
      rgba(76, 175, 80, 0.01) 75%,
      rgba(42, 146, 53, 0.01) 100%
    ),
    radial-gradient(circle at 20% 50%, rgba(42, 146, 53, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(76, 175, 80, 0.1) 0%, transparent 50%)
  `,
}));

// Floating background icons
const FloatingIcon = styled(Box)(({ theme }) => ({
  animation: `${float} 3s ease-in-out infinite`,
  position: 'absolute',
  opacity: 0.08,
  pointerEvents: 'none',
  '&.icon-1': {
    top: '8%',
    left: '8%',
    animationDelay: '0s',
  },
  '&.icon-2': {
    top: '15%',
    right: '12%',
    animationDelay: '1s',
  },
  '&.icon-3': {
    bottom: '12%',
    left: '15%',
    animationDelay: '2s',
  },
}));

// Hero section (matching Resources page exactly)
const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  background: 'transparent',
  borderRadius: '16px',
  padding: theme.spacing(3, 3),
  marginBottom: theme.spacing(3),
  overflow: 'hidden',
  textAlign: 'center', // ensure title + subtitle are centered
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2.5, 2),
  },
}));

// Main title (matching Resources page exactly)
const MainTitle = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(1.5rem, 3.5vw, 2.2rem)',
  fontWeight: 600,
  color: '#000000', // Black as requested
  textAlign: 'center',
  marginBottom: theme.spacing(2), // more breathing room under the main title
  letterSpacing: '-0.01em',
  lineHeight: 1.2,
}));

// Subtitle (matching Resources page exactly)
const SubTitle = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(0.9rem, 1.8vw, 1rem)',
  fontWeight: 400,
  color: '#6b7280',
  textAlign: 'center',
  lineHeight: 1.6,
  maxWidth: '560px',
  margin: '0 auto',
  marginTop: theme.spacing(0.5), // spacing from the main title
  opacity: 1,
}));

// Title with underline
const TitleWithUnderline = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-4px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '60px',
    height: '3px',
    background: 'linear-gradient(90deg, #2A9235 0%, #4CAF50 100%)',
    borderRadius: '2px',
  },
}));

// Newsletter form card
const NewsletterCard = styled(Card)(({ theme }) => ({
  borderRadius: '22px',
  background: `linear-gradient(145deg, 
    ${theme.palette.background.paper} 0%, 
    rgba(248, 249, 250, 0.8) 100%)`,
  border: `1px solid ${theme.palette.divider}`,
  backdropFilter: 'blur(20px)',
  overflow: 'hidden',
  position: 'relative',
  animation: `${fadeInUp} 0.6s ease-out`,
  maxWidth: '600px',
  margin: '0 auto',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #2A9235 0%, #4CAF50 100%)',
  },
}));

// Modern button styling
const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: theme.spacing(1.5, 4),
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
  color: '#fff',
  boxShadow: '0 6px 20px rgba(42, 146, 53, 0.25)',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 8px 25px rgba(42, 146, 53, 0.35)',
  },
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
    transition: 'left 0.5s',
  },
  '&:hover:before': {
    left: '100%',
  },
}));

// Benefits section
const BenefitsSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  padding: theme.spacing(1, 0),
  textAlign: 'center',
  width: '100%',
}));

// Benefit card
const BenefitCard = styled(Card)(({ theme }) => ({
  borderRadius: '18px',
  border: `2px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  transform: 'translateY(0) scale(1)',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
  height: '100%',
  '&:hover': {
    transform: 'translateY(-4px) scale(1.01)',
    boxShadow: '0 12px 40px rgba(42, 146, 53, 0.15)',
    borderColor: '#2A9235',
  },
}));

export default function Newsletter() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsVisible(true);
  }, []);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    console.log('Newsletter subscription attempted:', { name, email });
    alert(`Thank you for subscribing, ${name || 'valued learner'}! We'll send updates to ${email}.`);
    setName('');
    setEmail('');
  };

  return (
    <>
      <Helmet>
        <title>Subscribe to Spelling Hero Newsletter - Get Spelling Tips & Updates</title>
        <meta
          name="description"
          content="Join the Spelling Hero newsletter to receive exclusive spelling tips, educational content, practice words, and updates about our spelling learning platform!"
        />
        <link rel="canonical" href="https://spellinghero.app/newsletter"></link> 
      </Helmet>
      
      <Navbar />
      <MainContainer sx={{ 
        pt: { xs: 7, md: 8 }, 
        pb: 2, 
        backgroundColor: '#fafbfc',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Floating background icons */}
        <FloatingIcon className="icon-1">
          <School sx={{ fontSize: '80px', color: '#2A9235' }} />
        </FloatingIcon>
        <FloatingIcon className="icon-2">
          <Email sx={{ fontSize: '70px', color: '#4CAF50' }} />
        </FloatingIcon>
        <FloatingIcon className="icon-3">
          <AutoAwesome sx={{ fontSize: '75px', color: '#2A9235' }} />
        </FloatingIcon>

        <Container maxWidth="lg">
          <Fade in={isVisible} timeout={800}>
            <HeroSection>
              <TitleWithUnderline>
                <MainTitle variant="h1">
                  Newsletter Hub
                </MainTitle>
              </TitleWithUnderline>
              <SubTitle variant="h5">
                Join our community and get exclusive spelling tips, practice words, and learning strategies delivered to your inbox!
              </SubTitle>
            </HeroSection>
          </Fade>

          {/* Newsletter Form */}
          <Fade in={isVisible} timeout={1200}>
            <NewsletterCard elevation={0}>
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <TextField
                    label="Your Name (Optional)"
                    variant="outlined"
                    fullWidth
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                        },
                      },
                    }}
                  />
                  <TextField
                    type="email"
                    label="Your Email Address"
                    variant="outlined"
                    required
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                        },
                      },
                    }}
                  />
                  <ActionButton
                    type="submit"
                    variant="contained"
                    size="large"
                    startIcon={<Send />}
                    fullWidth
                  >
                    Subscribe Now
                  </ActionButton>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      textAlign: 'center', 
                      color: 'text.secondary',
                      fontSize: '13px'
                    }}
                  >
                    Join our community of successful learners. Unsubscribe anytime.
                  </Typography>
                </Box>
              </CardContent>
            </NewsletterCard>
          </Fade>

          {/* Benefits Section */}
          <BenefitsSection>
            <Fade in={isVisible} timeout={1400}>
              <Box sx={{ 
                textAlign: 'center', 
                mb: 2, 
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: 'clamp(1.2rem, 2.2vw, 1.6rem)',
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 1,
                    textAlign: 'center',
                    width: '100%'
                  }}
                >
                  What You'll Get
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '14px',
                    color: 'text.secondary',
                    lineHeight: 1.5,
                    maxWidth: '600px',
                    textAlign: 'center',
                    width: '100%'
                  }}
                >
                  Exclusive content designed to accelerate your spelling journey
                </Typography>
              </Box>
            </Fade>

            <Slide in={isVisible} direction="up" timeout={1600}>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                gap: { xs: 2, lg: 1.5 },
                maxWidth: '1200px',
                mx: 'auto'
              }}>
                <BenefitCard elevation={0}>
                  <CardContent sx={{ p: { xs: 2.5, lg: 2 }, textAlign: 'center' }}>
                    <Box sx={{ 
                      width: { xs: '60px', lg: '50px' }, 
                      height: { xs: '60px', lg: '50px' }, 
                      borderRadius: '16px',
                      background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: { xs: 2, lg: 1.5 }
                    }}>
                      <Star sx={{ fontSize: { xs: '28px', lg: '24px' }, color: '#fff' }} />
                    </Box>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600, 
                      color: 'text.primary', 
                      fontSize: { xs: '1.1rem', lg: '1rem' },
                      mb: { xs: 1, lg: 0.8 }
                    }}>
                      Weekly Word Mastery
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'text.secondary', 
                      lineHeight: 1.4, 
                      fontSize: { xs: '14px', lg: '13px' }
                    }}>
                      Curated spelling challenges and advanced vocabulary building exercises.
                    </Typography>
                  </CardContent>
                </BenefitCard>

                <BenefitCard elevation={0}>
                  <CardContent sx={{ p: { xs: 2.5, lg: 2 }, textAlign: 'center' }}>
                    <Box sx={{ 
                      width: { xs: '60px', lg: '50px' }, 
                      height: { xs: '60px', lg: '50px' }, 
                      borderRadius: '16px',
                      background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: { xs: 2, lg: 1.5 }
                    }}>
                      <Psychology sx={{ fontSize: { xs: '28px', lg: '24px' }, color: '#fff' }} />
                    </Box>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600, 
                      color: 'text.primary', 
                      fontSize: { xs: '1.1rem', lg: '1rem' },
                      mb: { xs: 1, lg: 0.8 }
                    }}>
                      Smart Learning Insights
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'text.secondary', 
                      lineHeight: 1.4, 
                      fontSize: { xs: '14px', lg: '13px' }
                    }}>
                      Personalized tips and cognitive strategies for accelerated learning.
                    </Typography>
                  </CardContent>
                </BenefitCard>

                <BenefitCard elevation={0}>
                  <CardContent sx={{ p: { xs: 2.5, lg: 2 }, textAlign: 'center' }}>
                    <Box sx={{ 
                      width: { xs: '60px', lg: '50px' }, 
                      height: { xs: '60px', lg: '50px' }, 
                      borderRadius: '16px',
                      background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: { xs: 2, lg: 1.5 }
                    }}>
                      <EmojiEvents sx={{ fontSize: { xs: '28px', lg: '24px' }, color: '#fff' }} />
                    </Box>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600, 
                      color: 'text.primary', 
                      fontSize: { xs: '1.1rem', lg: '1rem' },
                      mb: { xs: 1, lg: 0.8 }
                    }}>
                      Achievement Tracking
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'text.secondary', 
                      lineHeight: 1.4, 
                      fontSize: { xs: '14px', lg: '13px' }
                    }}>
                      Celebrate milestones and unlock exclusive learning rewards.
                    </Typography>
                  </CardContent>
                </BenefitCard>

                <BenefitCard elevation={0}>
                  <CardContent sx={{ p: { xs: 2.5, lg: 2 }, textAlign: 'center' }}>
                    <Box sx={{ 
                      width: { xs: '60px', lg: '50px' }, 
                      height: { xs: '60px', lg: '50px' }, 
                      borderRadius: '16px',
                      background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: { xs: 2, lg: 1.5 }
                    }}>
                      <Group sx={{ fontSize: { xs: '28px', lg: '24px' }, color: '#fff' }} />
                    </Box>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600, 
                      color: 'text.primary', 
                      fontSize: { xs: '1.1rem', lg: '1rem' },
                      mb: { xs: 1, lg: 0.8 }
                    }}>
                      Expert Community
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'text.secondary', 
                      lineHeight: 1.4, 
                      fontSize: { xs: '14px', lg: '13px' }
                    }}>
                      Connect with fellow learners and access expert guidance.
                    </Typography>
                  </CardContent>
                </BenefitCard>
              </Box>
            </Slide>
          </BenefitsSection>
        </Container>
      </MainContainer>
      <Footer />
    </>
  );
}